import React from "react";
import { Alert, Button, Icon } from "../../designSystem";

interface VerticalStepProps {
  isActive: boolean;
  isCompleted: boolean;
  children: React.ReactNode;
  step: number;
}

export const VerticalStep: React.FC<VerticalStepProps & React.HTMLAttributes<HTMLDivElement>> = ({
  isActive,
  step,
  isCompleted,
  children,
  style,
  ...rest
}) => {
  if (!isActive && !isCompleted) {
    return null;
  }

  return (
    <div
      className="tk-vertical-step"
      style={{
        padding: "10px 42px 14px 70px",
        position: "relative",
        ...style,
      }}
      {...rest}
    >
      <style>{`
      .tk-vertical-step::before {
        content: "";
        width: 1px;
        background: #E5E9ED;
        height: calc(100% - 40px);
        position: absolute;
        left: 20px;
        top: 43px;
      }
      .tk-vertical-step:last-child::before {
        display: none;
      }
      .tk-vertical-step h5 { margin-bottom: 20px; }
    `}</style>
      <span style={{ position: "absolute", left: 0, top: 3, fontSize: 0, lineHeight: 0 }}>
        {isCompleted ? (
          <svg
            width="41"
            height="41"
            viewBox="0 0 41 41"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle cx="20.5" cy="20.5" r="19.5" fill="var(--blue-900)" stroke="var(--blue-900)" strokeWidth="2" />
            <path
              d="M28.4353 16.5972L26.5728 14.7294C26.5102 14.6667 26.4253 14.6314 26.3367 14.6314C26.2481 14.6314 26.1631 14.6667 26.1005 14.7294L18.2343 22.601C18.1717 22.6637 18.0867 22.699 17.9981 22.699C17.9095 22.699 17.8246 22.6637 17.762 22.601L15.8995 20.7331C15.8369 20.6704 15.7519 20.6352 15.6633 20.6352C15.5747 20.6352 15.4898 20.6704 15.4272 20.7331L13.5647 22.601C13.4345 22.7312 13.4345 22.9424 13.5647 23.0726L17.762 27.2706C17.8246 27.3333 17.9095 27.3686 17.9981 27.3686C18.0867 27.3686 18.1717 27.3333 18.2343 27.2706L28.4353 17.0642C28.5624 16.9345 28.5624 16.7269 28.4353 16.5972Z"
              fill="var(--primary)"
            />
          </svg>
        ) : (
          <span
            style={{
              width: 40,
              height: 40,
              background: isActive ? "var(--blue-900)" : "#E5E9ED",
              color: isActive ? "var(--white)" : "currentColor",
              borderRadius: "50%",
              textAlign: "center",
              lineHeight: "40px",
              display: "block",
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            {step}
          </span>
        )}
      </span>
      {children}
    </div>
  );
};

export const VerticalStepContainer: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div style={{ maxWidth: 730 }}>{children}</div>
);

interface HeadingProps {
  heading: string;
  hideBorder?: boolean;
  btnFunction?: () => void;
  showBtn?: boolean;
  buttonText?: string;
}

export const Heading: React.FC<HeadingProps & React.HTMLAttributes<HTMLDivElement>> = ({
  heading,
  hideBorder,
  btnFunction,
  showBtn,
  buttonText,
  style,
  ...rest
}) => (
  <div
    style={{
      marginBottom: 16,
      display: "flex",
      alignItems: "center",
      ...style,
    }}
    {...rest}
  >
    <h5 style={{ margin: 0, whiteSpace: "nowrap", marginRight: 20 }}>{heading}</h5>
    {!hideBorder && <hr style={{ flex: "auto", margin: 0 }} />}
    {showBtn && (
      <span style={{ minWidth: 30, textAlign: "right", marginLeft: 20 }}>
        <Button variant="ghost" size="sm" onClick={() => btnFunction?.()} style={{ padding: 5, minWidth: 0 }}>
          {buttonText}
        </Button>
      </span>
    )}
  </div>
);

interface AddButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  buttonText: string;
}

export const AddButton: React.FC<AddButtonProps> = ({ buttonText, style, ...rest }) => (
  <Button variant="outline" size="sm" style={{ padding: "9px 15px", gap: 10, ...style }} {...rest}>
    <Icon name="plus" size={14} />
    {buttonText}
  </Button>
);

export const Warning: React.FC<{
  children?: React.ReactNode;
  style?: React.CSSProperties;
  severity?: "warning" | "error";
}> = ({ children, style, severity = "warning" }) => (
  <Alert
    tone={severity === "error" ? "error" : "warning"}
    style={{
      border: severity === "error" ? "2px solid var(--red)" : "2px solid #F69E1E",
      background: "transparent",
      marginBottom: 20,
      fontSize: 12,
      ...style,
    }}
  >
    {children}
  </Alert>
);

export const Label: React.FC<React.LabelHTMLAttributes<HTMLLabelElement>> = ({ style, ...rest }) => (
  <label style={{ cursor: "pointer", display: "block", fontSize: 12, ...style }} {...rest} />
);

export const ShareCalc: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr auto 1fr auto 1fr",
      columnGap: 10,
    }}
  >
    <style>{`.tk-share-calc > span { font-weight: 600; margin-top: 32px; }`}</style>
    <div className="tk-share-calc" style={{ display: "contents" }}>
      {children}
    </div>
  </div>
);

export const BoldLabel: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <b style={{ marginBottom: 5, display: "block" }}>{children}</b>
);

export const InputContainer: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div style={{ display: "flex", flexDirection: "column", marginRight: 15 }}>{children}</div>
);

export const Link: React.FC<React.AnchorHTMLAttributes<HTMLAnchorElement>> = ({ className, style, ...rest }) => (
  <a
    className={`u-text-primary u-cursor-pointer u-whitespace-nowrap${className ? ` ${className}` : ""}`}
    style={{ fontWeight: "bold", ...style }}
    {...rest}
  />
);

export const Hover: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ style, ...rest }) => (
  <div style={{ marginRight: 10, padding: "5px 10px 5px 20px", ...style }} {...rest} />
);

export const RadioWrapper: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ style, ...rest }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "row",
      marginBottom: 16,
      paddingTop: 12,
      paddingBottom: 12,
      marginLeft: -8,
      ...style,
    }}
    {...rest}
  />
);

export const IconWrapper: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div style={{ paddingTop: 1, fontSize: 0, lineHeight: 0 }}>{children}</div>
);

export const Skeleton: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, style, ...rest }) => (
  <div className={`u-skeleton${className ? ` ${className}` : ""}`} style={style} {...rest} />
);

export const SearchHeader: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div style={{ flex: 1, maxWidth: 400, marginRight: 16, marginLeft: 16 }}>{children}</div>
);

export const BoldText: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <b style={{ fontWeight: 700 }}>{children}</b>
);

export const NoHoldingsMessage: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties }> = ({
  children,
  style,
}) => (
  <div
    className="u-text-grey-mid"
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      height: "58%",
      textAlign: "center",
      fontFamily: "Montserrat",
      fontSize: 18,
      fontWeight: 600,
      ...style,
    }}
  >
    {children}
  </div>
);

export const NoResults: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <h6 className="u-text-grey-mid u-m-0" style={{ width: "100%", textAlign: "center" }}>
    {children}
  </h6>
);

export const GroundContainer: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      position: "absolute",
      bottom: 0,
      width: "100%",
    }}
  >
    {children}
  </div>
);
